import React from "react"
import Paper from "material-ui/Paper"
import { CircularProgress, Toggle } from "material-ui"
import AuthenticationStore from "../../stores/AuthenticationStore"
import HierarchySettingsStore from "../../stores/HierarchySettingsStore"
import AccountStore from "../../stores/AccountStore"
import TimecardsBody from "../../components/timecard/timecard_list/TimecardsBody"
import TimecardDateRange from "../../components/timecard/TimecardDateRange"
import autobind from "class-autobind"
import strings from "../../localization/strings"
import * as api from "../../api"
import moment from "moment"
import * as permissionHelper from "../../helpers/permissionHelper"
import { ITimecardDates, IUserFilterDocument, ISelectedFilterTemplateId, IUserFilter, IDate, IPayGroupTimecards, ITimecardFullData, IPayrollPayCalendar, EExceptionUITypes } from "../../models"
import ExceptionPunchSets from "../../components/exception_punchset/ExceptionPunchSets"
import * as utils from "../../helpers/utils"
import PayCalendarSelector from "../../components/pay_calendar/PayCalendarSelector"
import * as timecardHelper from "../../helpers/timecardHelper"
import AppStateStore from "../../stores/AppStateStore"
import * as ExceptionFilterActions from "../../actions/ExceptionFilterActions"
import ExceptionFilterStore from "../../stores/ExceptionFilterStore"
import * as payrollHelper from "../../helpers/payrollHelper"
import * as exceptionHelper from "../../helpers/exceptionHelper"
import UserFilter from "../../components/filter_template/employee/UserFilter"
import { EDefaultFilterValues } from "../../models"

interface IProps {
}
interface IState {
	loading: boolean
	total: number
	totalFilterAmount: number
	offset: number
	limit: number
	timecards: ITimecardFullData[]
	referenceDate: IDate
	timecardDates: ITimecardDates
	selectedPayrollPayCalendar: IPayrollPayCalendar
	showTable: boolean
	filterByDefaultHierarchy: boolean
	filterDirectReportsOnly: boolean
	filterByPunchHierarchy: boolean
	filterByException: boolean
	filterByTimecardValidationMessages: boolean
	selectedFilterTypes: EExceptionUITypes[]
	showTimecardDetails: boolean
	userFilter: IUserFilter
	selectedFilterTemplateId: ISelectedFilterTemplateId
	filterTemplates: IUserFilter[]
	isFilterDataLoaded: boolean
}

export default class Timecards extends React.Component<IProps, IState> {
	private punchSetExceptionLocalStorageKey = "ExceptionFilterTypeStringForTimecardPage"
	constructor(props: IProps) {
		super(props)
		const selectedPayrollPayCalendar = payrollHelper.getSelectedPayrollPayCalendar()
		const storedFilterByDefaultHierarchy = utils.getCookie("FilterTimecardByDefaultHierarchy")
		const storedFilterDirectReportsOnly = utils.getCookie("ShowDirectReportOnlyForTimecardsPage")
		const storedFilterByPunchHierarchy = utils.getCookie("FilterTimecardByPunchHierarchy")
		const storedFilterByExceptioin = utils.getCookie("FilterTimecardByException")
		const storedReferenceDate = utils.getCookie("TimecardReferenceDate")
		const storedFilterByTimecardValidationMessages = utils.getCookie("FilterByTimecardValidationMessages")
		const referenceDate = storedReferenceDate ? storedReferenceDate : moment()

		this.state = {
			loading: true,
			referenceDate,
			offset: 0,
			limit: 50,
			total: 0,
			totalFilterAmount: 0,
			timecards: [],
			timecardDates: timecardHelper.getTimecardDateByPayrollPayCalendar(referenceDate, selectedPayrollPayCalendar),
			selectedPayrollPayCalendar,
			showTable: true,
			filterByDefaultHierarchy: storedFilterByDefaultHierarchy === "true",
			filterDirectReportsOnly: storedFilterDirectReportsOnly === "true" || (AccountStore.getSupervisorDirectReportEnabled() && !permissionHelper.hasPermission(AuthenticationStore.Permissions.SupervisorDirectReport.ViewAllEmployees)),
			filterByPunchHierarchy: storedFilterByPunchHierarchy === "true",
			filterByException: storedFilterByExceptioin === "true",
			filterByTimecardValidationMessages: storedFilterByTimecardValidationMessages === "true",
			selectedFilterTypes: exceptionHelper.getSelectedExceptionFilterTypesByStorage(this.punchSetExceptionLocalStorageKey),
			showTimecardDetails: localStorage.getItem("showTimecardDetails") === "true",
			userFilter: null,
			selectedFilterTemplateId: EDefaultFilterValues.NONE,
			filterTemplates: [],
			isFilterDataLoaded: false
		}
		autobind(this)
	}

	componentDidMount() {
		document.title = `${strings.Timecards} | ${strings.Findd_Grandd_Central}`
		HierarchySettingsStore.on("global_change", this.loadTimecards)
		ExceptionFilterStore.on("refresh_page", this.exceptionFilterStoreChangeEvent)
		// Remove separate loadTimecards call and only call loadFilterTemplates
		this.loadFilterTemplates()
	}

	componentWillUnmount() {
		HierarchySettingsStore.removeListener("global_change", this.loadTimecards)
		ExceptionFilterStore.removeListener("refresh_page", this.exceptionFilterStoreChangeEvent)
	}

	exceptionFilterStoreChangeEvent() {
		ExceptionFilterStore.getRefreshPage() && this.getTimcards()
	}

	tableReload() {
		this.setState({ showTable: false }, () => { this.setState({ showTable: true }) })
	}

	getTimcards(cb = null) {
		this.state.isFilterDataLoaded &&
			api.getTimecards(
				AuthenticationStore.getCookieAccountId(),
				this.state.offset / this.state.limit,
				this.state.limit,
				payrollHelper.getPayGroupIdsByPayrollPayCalendar(this.state.selectedPayrollPayCalendar),
				HierarchySettingsStore.getGlobalLevelItemFilterId(),
				moment(this.state.referenceDate).utc(true).format(),
				this.state.filterDirectReportsOnly,
				this.state.filterByPunchHierarchy,
				this.state.filterByDefaultHierarchy,
				this.state.filterByException,
				exceptionHelper.getExceptionTypes(exceptionHelper.getValidExceptionTypesForFilterTimecards(this.state.selectedFilterTypes)),
				this.state.filterByTimecardValidationMessages,
				this.state.userFilter
			).then(res => {
				const payGroupTimecards: IPayGroupTimecards = res.data
				this.setState({
					loading: false,
					total: payGroupTimecards.ObjectTimecardSearch.TotalDocumentCount,
					totalFilterAmount: payGroupTimecards.ObjectTimecardSearch.TotalFilterDocumentCount,
					timecards: payGroupTimecards.ObjectTimecardSearch.Documents
				})
				AppStateStore.getProgressSnackbarContent() === "loading" &&
					AppStateStore.setProgressSnackbarContent(null)
				cb && typeof cb === "function" && cb()
			})
	}

	reloadTimecards(cb = null) {
		ExceptionFilterActions.refreshExceptionFilter()
		this.getTimcards(cb)
	}

	loadTimecards() {
		AppStateStore.setTextProgressSnackbarContent({ progressText: strings.Loading_Timecards }, () =>
			this.setState({ loading: true }, () => {
				this.getTimcards(() => {
					AppStateStore.setTextProgressSnackbarContent(null)
					this.tableReload()
				})
			})
		)
	}

	async loadFilterTemplates() {
		try {
			const res = await api.getFilters(AuthenticationStore.getLoggedInUser().Id)
			const result = res.data as IUserFilterDocument
			const selectedFilterTemplateId = result && result.SelectedTimecardUserFilter
				? result.SelectedTimecardUserFilter
				: EDefaultFilterValues.NONE
			const filterTemplates = result && result.TimecardUserFilters ? result.TimecardUserFilters : []

			// Set state and then load timecards after filters are loaded
			this.setState({
				filterTemplates,
				selectedFilterTemplateId,
				// If there's a selected filter, set it
				userFilter: selectedFilterTemplateId !== EDefaultFilterValues.NONE
					? filterTemplates.find(i => i.Id === selectedFilterTemplateId)
					: null,
				isFilterDataLoaded: true  // Set this to true after filters are loaded
			})
			this.loadTimecards()
		} catch (error) {
			console.error('Error loading filter templates:', error)
			this.setState({
				filterTemplates: [],
				isFilterDataLoaded: true  // Still set to true even on error to allow rendering
			})
		}
	}

	handleSelectFilterTemplate(selectedFilterTemplateId: ISelectedFilterTemplateId) {
		// Don't proceed with API call and search if "Create New" is selected
		if (selectedFilterTemplateId === EDefaultFilterValues.CREATE_NEW) {
			this.setState({ selectedFilterTemplateId })
			return
		}

		const selectedFilterTemplate = this.state.filterTemplates.find(i => i.Id === selectedFilterTemplateId)
		const userFilter = (selectedFilterTemplateId === EDefaultFilterValues.NONE) ? null : selectedFilterTemplate

		api.setSelectedTimecardUserFilter(AuthenticationStore.getLoggedInUser().Id, (selectedFilterTemplateId === EDefaultFilterValues.NONE) ? "" : selectedFilterTemplateId as string)
			.then(() => {
				this.setState({
					selectedFilterTemplateId,
					offset: 0,
					userFilter
				}, () => this.loadTimecards())
			})
	}

	handleChangeFilterTemplates(filterTemplates: IUserFilter[]) {
		this.setState({ filterTemplates })
	}

	render() {
		return (
			<div className="page-container Timecards" style={{ position: "relative" }}>
				<div className="page-container-parent" style={{ minWidth: 1275 }}>
					<Paper zDepth={1} className="page-paper" style={{ padding: 20, marginBottom: 20 }}>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							{/* Left side - Filter */}
							<div>
								<div style={{ marginBottom: 15 }}>
									<UserFilter
										filterTemplates={this.state.filterTemplates}
										selectedFilterTemplateId={this.state.selectedFilterTemplateId}
										handleSelectFilterTemlate={this.handleSelectFilterTemplate}
										handleChangeFilterTemplates={this.handleChangeFilterTemplates}
										isTimecardFilter={true}
									/>
								</div>
								<div style={{ marginTop: 15 }}>
									<TimecardDateRange
										referenceDate={this.state.referenceDate}
										payCalendar={this.state.selectedPayrollPayCalendar}
										handleChangeReferenceDate={(referenceDate, timecardDates) => {
											localStorage.setItem("findd_TimecardReferenceDate", moment(referenceDate).format())
											this.setState({
												referenceDate,
												timecardDates
											}, () => {
												this.loadTimecards()
											})
										}}
									/>
								</div>
							</div>

							{/* Center - Date Header and Pay Calendar */}
							<div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "0 20px", gap: 10 }}>
								<span style={{ fontSize: 20, fontWeight: "bold" }}>
									{moment(this.state.timecardDates.StartDate).format("ddd MMM. DD, YYYY")} &mdash; {moment(this.state.timecardDates.EndDate).format("ddd MMM. DD, YYYY")}
								</span>
								<PayCalendarSelector
									width={250}
									selectedPayrollPayCalendar={this.state.selectedPayrollPayCalendar}
									handleSelect={selectedPayrollPayCalendar => {
										this.setState({
											selectedPayrollPayCalendar,
											timecardDates: timecardHelper.getTimecardDateByPayrollPayCalendar(this.state.referenceDate, selectedPayrollPayCalendar)
										}, () => this.loadTimecards())
									}}
								/>
							</div>

							{/* Right side - Toggles */}
							<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
								{AccountStore.getSupervisorDirectReportEnabled() && permissionHelper.hasPermission(AuthenticationStore.Permissions.SupervisorDirectReport.ViewAllEmployees) &&
									<Toggle
										data-intercom-target={"menu-Timecards-Filter-Toggle-ShowDirectReportsOnly"}
										toggled={this.state.filterDirectReportsOnly}
										onToggle={(e, value) => {
											this.setState({ filterDirectReportsOnly: value }, () => this.loadTimecards())
											AccountStore.getSupervisorDirectReportEnabled() && localStorage.setItem("findd_ShowDirectReportOnlyForTimecardsPage", value)
										}}
										labelPosition="left"
										label={strings.Show_Direct_Reports_Only}
										style={{ marginBottom: 3 }}
										labelStyle={{ whiteSpace: "nowrap" }}
									/>
								}
								<Toggle
									data-intercom-target={"menu-Timecards-Filter-Toggle-FilterByValidationMessages"}
									toggled={this.state.filterByTimecardValidationMessages}
									onToggle={(e, value) => {
										this.setState({ filterByTimecardValidationMessages: value }, () => this.loadTimecards())
										localStorage.setItem("findd_FilterByTimecardValidationMessages", value)
									}}
									labelPosition="left"
									label={strings.Filter_By_Timecard_Validation_Messages}
									style={{ marginBottom: 3 }}
									labelStyle={{ whiteSpace: "nowrap" }}
								/>
								{/* filterByDefaultHierarchy and filterByPunchHierarchy must be mutually exclusive. */}
								<Toggle
									data-intercom-target={"menu-Timecards-Filter-Toggle-FilterByDefaultHierarchy"}
									toggled={this.state.filterByDefaultHierarchy}
									onToggle={(e, value) => {
										const filterByDefaultHierarchy = value
										let filterByPunchHierarchy = this.state.filterByPunchHierarchy
										if (filterByDefaultHierarchy) {
											filterByPunchHierarchy = false
										}
										this.setState({ filterByDefaultHierarchy, filterByPunchHierarchy }, () => this.loadTimecards())
										localStorage.setItem("findd_FilterTimecardByDefaultHierarchy", filterByDefaultHierarchy)
										localStorage.setItem("findd_FilterTimecardByPunchHierarchy", filterByPunchHierarchy as any)
									}}
									labelPosition="left"
									label={strings.Filter_By_Default_Hierarchy}
									style={{ marginBottom: 3 }}
									labelStyle={{ whiteSpace: "nowrap" }}
								/>
								<Toggle
									data-intercom-target={"menu-Timecards-Filter-Toggle-FilterByPunchHierarchy"}
									toggled={this.state.filterByPunchHierarchy}
									onToggle={(e, value) => {
										let filterByDefaultHierarchy = this.state.filterByDefaultHierarchy
										const filterByPunchHierarchy = value
										if (filterByPunchHierarchy) {
											filterByDefaultHierarchy = false
										}
										this.setState({ filterByDefaultHierarchy, filterByPunchHierarchy }, () => this.loadTimecards())
										localStorage.setItem("findd_FilterTimecardByDefaultHierarchy", filterByDefaultHierarchy as any)
										localStorage.setItem("findd_FilterTimecardByPunchHierarchy", filterByPunchHierarchy)
									}}
									labelPosition="left"
									label={strings.Filter_By_Punch_Hierarchy}
									style={{ marginBottom: 3 }}
									labelStyle={{ whiteSpace: "nowrap" }}
								/>
								<Toggle
									data-intercom-target={"menu-Timecards-Filter-Toggle-FilterByException"}
									toggled={this.state.filterByException}
									onToggle={(e, value) => {
										this.setState({ filterByException: value }, () => this.loadTimecards())
										localStorage.setItem("findd_FilterTimecardByException", value)
									}}
									labelPosition="left"
									label={strings.Filter_By_Exception}
									style={{ marginBottom: 3 }}
									labelStyle={{ whiteSpace: "nowrap" }}
								/>
							</div>
						</div>
					</Paper>
					{this.state.isFilterDataLoaded &&
						<ExceptionPunchSets
							isPayroll={false}
							payGroupIds={payrollHelper.getPayGroupIdsByPayrollPayCalendar(this.state.selectedPayrollPayCalendar)}
							referenceDate={this.state.referenceDate}
							localStorageKey={this.punchSetExceptionLocalStorageKey}
							filterDirectReportsOnly={this.state.filterDirectReportsOnly}
							filterByDefaultHierarchy={this.state.filterByDefaultHierarchy}
							filterByPunchHierarchy={this.state.filterByPunchHierarchy}
							viewPayrollByJob={false}
							selectedFilterTypes={this.state.selectedFilterTypes}
							handleSelectSelectedFilterTypes={selectedFilterTypes => {
								const oldValidExceptionTypesForFilterTimecards = exceptionHelper.getValidExceptionTypesForFilterTimecards(this.state.selectedFilterTypes)
								this.setState({ selectedFilterTypes }, () => {
									if (this.state.filterByException) {
										const newValidExceptionTypesForFilterTimecards = exceptionHelper.getValidExceptionTypesForFilterTimecards(selectedFilterTypes)
										oldValidExceptionTypesForFilterTimecards.length !== newValidExceptionTypesForFilterTimecards.length &&
											this.loadTimecards()
									}
								})
							}}
							userFilter={this.state.userFilter}
						/>}

					<Paper zDepth={1} className="page-paper" style={{ marginTop: 0 }}>
						<div style={{ display: "flex", justifyContent: "space-between", paddingLeft: 25, paddingRight: 25, paddingTop: 7, paddingBottom: 7 }}>
							<div style={{ fontSize: 18, fontWeight: 600 }}>
								{`${strings.Timecards}: ${this.state.loading ? "" : this.state.total}`}
							</div>
							<Toggle
								data-intercom-target={`menu-Employees-TimecardDetails-Toggle`}
								toggled={this.state.showTimecardDetails}
								onToggle={() => {
									this.setState({ showTimecardDetails: !this.state.showTimecardDetails }, () => {
										localStorage.setItem("showTimecardDetails", this.state.showTimecardDetails ? "true" : "false")
									}
									)
								}}
								labelPosition="left"
								label={strings.Timecard_Details}
								style={{ maxWidth: 200 }}
							/>
						</div>
						<div style={{ borderBottom: "solid 1px rgb(224, 224, 224)", height: 1 }} />
						{this.state.loading ?
							<div className="spinner" style={{ paddingBottom: "2em" }}>
								<CircularProgress />
							</div> :
							<div>
								{this.state.showTable &&
									<TimecardsBody
										referenceDate={this.state.referenceDate}
										userFilter={this.state.userFilter}
										timecardDates={this.state.timecardDates}
										timecards={this.state.timecards}
										totalTimecards={this.state.total}
										total={this.state.totalFilterAmount}
										offset={this.state.offset}
										limit={this.state.limit}
										limitOptions={[50, 100, 200]}
										onPageClick={offset => this.setState({ offset }, () => this.loadTimecards())}
										onLimitChange={limit => this.setState({ limit }, () => this.loadTimecards())}
										reLoadTimecards={this.reloadTimecards}
										reloadTable={this.loadTimecards}
										selectedPayrollPayCalendar={this.state.selectedPayrollPayCalendar}
										filterByDefaultHierarchy={this.state.filterByDefaultHierarchy}
										filterDirectReportsOnly={this.state.filterDirectReportsOnly}
										filterByPunchHierarchy={this.state.filterByPunchHierarchy}
										filterByException={this.state.filterByException}
										filterExceptions={exceptionHelper.getExceptionTypes(exceptionHelper.getValidExceptionTypesForFilterTimecards(this.state.selectedFilterTypes))}
										showTimecardDetails={this.state.showTimecardDetails}
									/>
								}
							</div>
						}
					</Paper>
				</div>
			</div >
		)
	}
}
